// (c) chaehongee. Confidential and proprietary.

import { useCallback, useMemo } from 'react'
import type { NodeResponse } from 'models-ts/node'
import type { CanvasStateService } from '../services/CanvasStateService'
import type { useFraming } from '../features/framing'
import type { DrawOverlayHandle } from '../features/framing'
import type { Rect } from '../features/items/types'

interface CoordinatorFeatures {
    framing: ReturnType<typeof useFraming>
    brushEnabled: boolean
    drawTargetId: string | null
    brushToolbarProps: {
        canUndo: boolean
        canRedo: boolean
        onUndo: () => void
        onRedo: () => void
    }
    history: {
        undo: () => void
        redo: () => void
        clear: () => void
    }
}

interface CoordinatorDeps {
    session: { id: string; nodes?: NodeResponse[] } | null
    stateService: CanvasStateService
    resetFraming: () => void
    resetModes: () => void
    showAll: () => void
    hideAll: (ids: string[]) => void
    applyLayout: () => void
    commitLayoutPositions: () => void
    drawHandleRef: React.RefObject<DrawOverlayHandle | null>
    clearLocalSelection: () => void
    canvasItemsState: Array<{ id: string; src: string; mediaType?: string }>
    resetToolMode?: () => void
}

export interface CanvasCoordinator {
    // Cross-feature callbacks
    onImageRectChange: (id: string, rect: Rect) => void
    getDrawOverlayForId: (id: string) => Promise<Blob | null>
    hasDrawingChangesForId: (id: string) => boolean

    // Undo/Redo routing
    handleUndo: () => void
    handleRedo: () => void

    // Complex workflows
    handleAutoLayout: () => void
    handleClearSelection: () => void
}

/**
 * Canvas Coordinator Hook
 * 
 * Central orchestrator for cross-feature communication and workflows.
 * Eliminates ref-based communication between features.
 * 
 * Benefits:
 * - Single place for feature coordination logic
 * - No circular dependencies via refs
 * - Clear, testable coordination patterns
 * - Explicit dependency graph
 */
export function useCanvasCoordinator(
    features: CoordinatorFeatures,
    deps: CoordinatorDeps
): CanvasCoordinator {
    const { framing, brushEnabled, drawTargetId, brushToolbarProps, history } = features
    const {
        session,
        stateService,
        resetFraming,
        resetModes,
        showAll,
        hideAll,
        applyLayout,
        commitLayoutPositions,
        drawHandleRef,
        clearLocalSelection,
        canvasItemsState,
        resetToolMode,
    } = deps

    // ------------------------------------------------------------------------
    // Cross-Feature Event Handlers
    // ------------------------------------------------------------------------

    // Sync image rect changes to framing system
    const onImageRectChange = useCallback(
        (id: string, rect: Rect) => {
            if (framing.syncImageRect) {
                framing.syncImageRect(id, id, rect)
            }
        },
        [framing]
    )

    // Get drawing overlay blob for a specific image
    const getDrawOverlayForId = useCallback(
        async (id: string): Promise<Blob | null> => {
            if (id !== drawTargetId) return null
            const blob = await drawHandleRef.current?.exportOverlayBlob()
            return blob ?? null
        },
        [drawTargetId, drawHandleRef]
    )

    // Check if image has drawing changes
    const hasDrawingChangesForId = useCallback(
        (id: string): boolean => {
            if (id !== drawTargetId) return false
            return drawHandleRef.current?.hasChanges() ?? false
        },
        [drawTargetId, drawHandleRef]
    )

    // ------------------------------------------------------------------------
    // Undo/Redo Routing
    // ------------------------------------------------------------------------

    const handleUndo = useCallback(() => {
        // Priority 1: Drawing overlay undo (if active and ref available)
        if (drawTargetId && drawHandleRef.current) {
            drawHandleRef.current.undo()
            return
        }
        // Priority 2: Brush mode undo
        if (brushEnabled && brushToolbarProps.canUndo) {
            brushToolbarProps.onUndo()
            return
        }
        // Priority 3: Viewport history undo
        history.undo()
    }, [drawTargetId, brushEnabled, brushToolbarProps, history, drawHandleRef])

    const handleRedo = useCallback(() => {
        // Priority 1: Drawing overlay redo (if active and ref available)
        if (drawTargetId && drawHandleRef.current) {
            drawHandleRef.current.redo()
            return
        }
        // Priority 2: Brush mode redo
        if (brushEnabled && brushToolbarProps.canRedo) {
            brushToolbarProps.onRedo()
            return
        }
        // Priority 3: Viewport history redo
        history.redo()
    }, [drawTargetId, brushEnabled, brushToolbarProps, history, drawHandleRef])

    // ------------------------------------------------------------------------
    // Complex Workflows
    // ------------------------------------------------------------------------

    const handleAutoLayout = useCallback(() => {
        // Clear session positions
        if (session?.id) {
            stateService.clearSessionPositions(session.id)
        }

        // Show all hidden items
        showAll()

        // Set canvas items from session nodes
        const nodes = (session?.nodes ?? []).filter(
            (n: NodeResponse) => typeof n?.image_path === 'string' && n.image_path
        )
        if (nodes.length > 0) {
            const items = nodes.map((n: NodeResponse) => ({
                id: n.id,
                src: n.image_path as string,
                mediaType: n.type ?? 'image',
            }))
            if (session?.id) stateService.setItems(session.id, items)
        }

        // Reset feature states
        resetFraming()
        resetModes()
        resetToolMode?.()

        // Apply layout with timing coordination
        requestAnimationFrame(() => {
            applyLayout()
            setTimeout(() => commitLayoutPositions(), 32)
        })
    }, [
        session?.id,
        session?.nodes,
        stateService,
        showAll,
        applyLayout,
        resetFraming,
        resetModes,
        resetToolMode,
        commitLayoutPositions,
    ])

    const handleClearSelection = useCallback(() => {
        // Hide all current canvas items
        const currentIds = canvasItemsState.map((it) => it.id)
        hideAll(currentIds)

        // Clear all state
        if (session?.id) {
            stateService.clearItems(session.id)
            stateService.clearSelection(session.id)
        }
        clearLocalSelection()
        resetFraming()
        resetToolMode?.()
    }, [canvasItemsState, hideAll, stateService, clearLocalSelection, resetFraming, resetToolMode, session?.id])

    // ------------------------------------------------------------------------
    // Return Coordinator API
    // ------------------------------------------------------------------------

    return useMemo(
        () => ({
            onImageRectChange,
            getDrawOverlayForId,
            hasDrawingChangesForId,
            handleUndo,
            handleRedo,
            handleAutoLayout,
            handleClearSelection,
        }),
        [
            onImageRectChange,
            getDrawOverlayForId,
            hasDrawingChangesForId,
            handleUndo,
            handleRedo,
            handleAutoLayout,
            handleClearSelection,
        ]
    )
}

